import { ChangeDetectorRef, Component, ElementRef, ViewChild } from '@angular/core';
import { FormBuilder, FormGroup, Validators, FormArray, ReactiveFormsModule, FormsModule, AbstractControl, ValidationErrors, ValidatorFn } from '@angular/forms';
import { CommonModule, DatePipe } from '@angular/common';
import { RickService } from '../service/rick.service';
import { CustomValidationService } from '../service/custom-validation.service';
import { DataSharingService } from '../service/data-sharing.service';
import { Router, RouterOutlet } from '@angular/router';

@Component({
  selector: 'app-model-maintenance',
  standalone: true,
  imports: [ReactiveFormsModule, CommonModule, RouterOutlet, FormsModule],
  templateUrl: './model-maintenance.component.html',
  styleUrl: './model-maintenance.component.scss',
  providers: [RickService, DatePipe]
})
export class ModelMaintenanceComponent {
  @ViewChild('errorMessageDiv') errorDiv!: ElementRef;
  @ViewChild('successMessageDiv') successDiv!: ElementRef;
  maintenanceForm: FormGroup = new FormGroup({});
  customerid: any
  state: any;
  store: any;
  lot: any;
  modelCustomerData: any;
  submitted: boolean = false;
  todayDate: any;
  consignTypes: Array<any> = [{
    name: 'CASE',
    value: 'C'
  },
  {
    name: 'PIECES',
    value: 'P'
  }];
  isSaved: boolean = false;
  isEdit: boolean = false;
  isView: boolean = false;
  isCopy: boolean = false;

  constructor(private fb: FormBuilder, private rickService: RickService,
    private customValidator: CustomValidationService, private datePipe: DatePipe,
    private dataSharingService: DataSharingService, private router: Router,
    private readonly changeDetectorRef: ChangeDetectorRef) {
    this.todayDate = this.datePipe.transform(new Date(), 'yyyy-MM-dd');
  }

  ngOnInit() {
    this.maintenanceForm = this.fb.group({
      customer: ['', Validators.required],
      state: ['', Validators.required],
      startdate: ['', [Validators.required], this.futureDateValidator.bind(this)],
      enddate: ['', Validators.required],
      store: ['', Validators.required],
      lot: ['', Validators.required],
      modelCustomer: [''],
      modelState: [''],
      modelStore: [''],
      modelLot: [''],
      consignType: ['C'],
      orderModelDetail: this.fb.array([this.sizeDetailsGroup()])
    }, { validator: this.customValidator.compareDates('startdate', 'enddate') });
    this.dataSharingService.currentData.subscribe((data) => {
      this.modelCustomerData = data;
      if (this.router.url === '/view') {
        this.isView = true;
        this.setupMaintenanceData();
      } else if (this.router.url === '/copy') {
        this.isCopy = true;
        this.setupCopyMaintenanceData();
      } else {
        if (this.modelCustomerData && (Object.keys(this.modelCustomerData).length !== 0)) {
          this.isEdit = true;
          this.setupMaintenanceData();
        } else {
          this.isEdit = false;
        }
      }
    });
  }


  get orderModelDetail(): FormArray {
    return this.maintenanceForm.controls["orderModelDetail"] as FormArray;
  }

  cancelPage() {
    this.router.navigate(['/dashboard']);
  }

  futureDateValidator(control: AbstractControl): any {
    return new Promise(resolve => {
      if (!control.value || this.isEdit) {
        resolve(null);
      }
      resolve(control.value >= this.todayDate ? null : { inValidStartDate: true });
    });
  }

  sizeDetailsGroup() {
    const index = this.maintenanceForm.controls["orderModelDetail"] ? this.maintenanceForm.controls["orderModelDetail"].value.length : 0;
    return this.fb.group({
      dim1: ['', Validators.required],
      dim2: ['', Validators.required],
      qty: ['', [
        Validators.required,
        Validators.pattern("^[0-9]*$")
      ]],
      newQty: ['', [
        Validators.pattern("^[0-9]*$")
      ]],
      existingField: [false],
    }, { validator: this.duplicateSizeCheck('dim1', 'dim2', index) });
  }

  addSizeDetails() {
    const sizeDetailsForm: FormGroup = this.sizeDetailsGroup();
    this.orderModelDetail.push(sizeDetailsForm);
  }

  deleteSizeDetails(index: number) {
    this.orderModelDetail.removeAt(index);
  }

  duplicateSizeCheck(dim1: any, dim2: any, index: number) {
    return (formGroup: FormGroup<any>) => {
      const dim1Control = formGroup.controls[dim1];
      const dim2Control = formGroup.controls[dim2];

      if (!dim1Control?.value || !dim2Control?.value) {
        return null;
      }
      const combinedValue = dim1Control.value + '-' + dim2Control.value;
      const attributes: any[] = [];
      (this.maintenanceForm.controls.orderModelDetail as FormArray).controls.forEach((value, i) => {
        attributes.push((value as FormGroup).controls[dim1].value + '-' + (value as FormGroup).controls[dim2].value);
      });
      (this.maintenanceForm.controls.orderModelDetail as FormArray).controls.forEach((value, i) => {
        const combinedValue = (value as FormGroup).controls[dim1].value + '-' + (value as FormGroup).controls[dim2].value;
        attributes.indexOf(combinedValue) >= 0 && attributes.indexOf(combinedValue) < i && index != i
          ? value.setErrors({ duplicateName: true })
          : value.setErrors(null);
      })
      return attributes.indexOf(combinedValue) >= 0 && attributes.indexOf(combinedValue) < index
        ? { duplicateName: true }
        : null;
    };

  }

  addModelData() {
    this.submitted = true;
    if (this.maintenanceForm.valid) {
      let data = {
        "modelsIdDto": {
          "edpNumberDto" : {
            "customer": this.maintenanceForm.controls.customer.value,
            "state": this.maintenanceForm.controls.state.value,
            "store": this.maintenanceForm.controls.store.value
          },
          "lot": this.maintenanceForm.controls.lot.value
        },
        "startDate": this.maintenanceForm.controls.startdate.value,
        "endDate": this.maintenanceForm.controls.enddate.value,
        "consignmentType": this.maintenanceForm.controls.consignType.value,
        "modelCustomer": this.maintenanceForm.controls.modelCustomer.value,
        "modelState": this.maintenanceForm.controls.modelState.value,
        "modelStore": this.maintenanceForm.controls.modelStore.value,
        "modelLot": this.maintenanceForm.controls.modelLot.value,
        "lotDatas": this.createLotDataList()
      }

      let postURL = this.isEdit ? this.rickService.updateCustomerData(data) : this.rickService.addCustomerData(data);
      postURL.subscribe((res) => {
        this.isSaved = true;
        this.changeDetectorRef.detectChanges();
        this.errorDiv.nativeElement.scrollIntoView();
      }, (error) => {
        this.maintenanceForm?.setErrors({ invalid: error?.error?.errorMessages[0] });
        this.changeDetectorRef.detectChanges();
        this.errorDiv.nativeElement.scrollIntoView();
      });
    }

  }

  createLotDataList() {
    let data: any = [];
    this.maintenanceForm.controls.orderModelDetail.value.map((value: any) => {
      data.push({
        "lotDataId": {
          "szDim1": value.dim1,
          "szDim2": value.dim2,
          "lot": this.maintenanceForm.controls.lot.value,
          "edpNumberDto": {
            "customer": this.maintenanceForm.controls.customer.value,
            "state": this.maintenanceForm.controls.state.value,
            "store": this.maintenanceForm.controls.store.value
          }          
        },
        "modelQty": value.qty,
        "newModelQty": this.isEdit ? value.newQty : undefined,
        "customer": this.maintenanceForm.controls.customer.value,
        "state": this.maintenanceForm.controls.state.value,
        "store": this.maintenanceForm.controls.store.value,
        "lot": this.maintenanceForm.controls.lot.value
      })
    });
    return data;

  }

  checkSizeDetailsErrors(field: string, errorType: string, index: number) {
    if (errorType == 'duplicateName') {
      return (this.submitted && ((this.maintenanceForm.controls.orderModelDetail as FormArray).controls[index] as FormGroup).errors?.[errorType]) ? true : false;
    } else {
      return (this.submitted && ((this.maintenanceForm.controls.orderModelDetail as FormArray).controls[index] as FormGroup).controls[field].errors?.[errorType]) ? true : false;
    }
  }

  setupMaintenanceData() {
    let lotDetArray: any = [];
    this.modelCustomerData.lotDatas.forEach((element: any, index: number) => {
      lotDetArray.push({
        "dim1": element.lotDataId.szDim1,
        "dim2": element.lotDataId.szDim2,
        "qty": element.modelQty,
        "newQty": element.newModelQty,
        "existingField": true
      });
      if (index != 0) {
        this.addSizeDetails();
      }
    });
    this.maintenanceForm.patchValue({
      customer: this.modelCustomerData.modelsIdDto?.edpNumberDto.customer,
      state: this.modelCustomerData.modelsIdDto?.edpNumberDto.state,
      startdate: this.modelCustomerData.startDate,
      enddate: this.modelCustomerData.endDate,
      store: this.modelCustomerData.modelsIdDto?.edpNumberDto.store,
      lot: this.modelCustomerData.modelsIdDto.lot,
      consignType: this.modelCustomerData.consignmentType,
      modelCustomer: this.modelCustomerData.modelCustomer,
      modelState: this.modelCustomerData.modelState,
      modelStore: this.modelCustomerData.modelStore,
      modelLot: this.modelCustomerData.modelLot,
    });
    (this.maintenanceForm.controls["orderModelDetail"] as FormArray).patchValue(lotDetArray);
  }

  setupCopyMaintenanceData() {
    let lotDetArray: any = [];
    this.modelCustomerData.lotDatas.forEach((element: any, index: number) => {
      lotDetArray.push({
        "dim1": element.lotDataId.szDim1,
        "dim2": element.lotDataId.szDim2,
        "qty": element.modelQty,
        "newQty": element.newModelQty,
        "existingField": true
      });
      if (index != 0) {
        this.addSizeDetails();
      }
    });
    this.maintenanceForm.patchValue({
      modelCustomer: this.modelCustomerData.modelsIdDto?.edpNumberDto.customer,
      modelState: this.modelCustomerData.modelsIdDto?.edpNumberDto.state,
      modelStore: this.modelCustomerData.modelsIdDto?.edpNumberDto.store,
      modelLot: this.modelCustomerData.modelsIdDto.lot,
      startdate: this.modelCustomerData.startDate,
      enddate: this.modelCustomerData.endDate,
      consignType: this.modelCustomerData.consignmentType
    });
    (this.maintenanceForm.controls["orderModelDetail"] as FormArray).patchValue(lotDetArray);
  }

  isDisabledField(index: number) {
    return (((this.isEdit || this.isCopy) && ((this.maintenanceForm.controls.orderModelDetail as FormArray).controls[index] as FormGroup).controls['existingField'].value) || this.isView) ? true : false;
  }

  isExistingField(index: number) {
    return ((this.maintenanceForm.controls.orderModelDetail as FormArray).controls[index] as FormGroup).controls['existingField'].value ? true : false;
  }
}
